/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import { FC, memo, ReactNode } from 'react';
import { secondsToTime } from '@/lib/utils';
import { Comment, Icon, VersionValue } from './NewTypes';

export interface ChatLineProps {
    vodId: string;
    line: Comment;
    onTimestampClicked: (timestamp: number) => void;
    badgeMap: Map<string, VersionValue>;
    emoteMap: Map<string, Icon>;
}

const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/ig;

export const ChatMessage: FC<ChatLineProps> = memo(({ vodId, line, onTimestampClicked, badgeMap, emoteMap }) => {
    const ret: ReactNode[] = [];
    const ts = secondsToTime(line.content_offset_seconds / 60);

    const messageBadges: ReactNode[] = [];
    line.message.user_badges.forEach((b, i) => {
        const twitchBadge = badgeMap.get(`${b._id}-${b.version}`);
        if (!twitchBadge) {
            return;
        }

        messageBadges.push(
            <img
              key={`${b._id}-${b.version}-${i}`}
              className="badge"
              src={`data:image/jpeg;base64,${twitchBadge?.bytes}`}
              alt={twitchBadge?.description}
              title={twitchBadge?.description}
              width={28}
              height={28}
            />
        );
    });

    const msg: ReactNode[] = [];
    line.message.fragments.forEach((frag, idx) => {
        const { emoticon } = frag;
        if (emoticon) {
            const emote = emoteMap.get(emoticon.emoticon_id);
            if (emote) {
                msg.push(
                    <img
                      key={`${line._id}-${idx}-${emote.id}`}
                      className="emote"
                      src={`/api/vods/emotes/${vodId}/${emote.id}`}
                      alt={emote.name ?? '-'}
                      title={emote.name ?? '-'}
                      width={emote.width}
                      height={emote.height}
                    />
                );
            }
        } else {
            const words = frag.text.split(' ');
            let buffer: string[] = [];

            const flushBuffer = (wordIdx: number) => {
                if (buffer.length > 0) {
                    msg.push(
                        <span key={`${line._id}-${idx}-${wordIdx}-text`} className="word">
                            {buffer.join(' ')}
                        </span>
                    );
                    buffer = [];
                }
            };

            words.forEach((word, wordIdx) => {
                const em = emoteMap.get(word);
                if (em) {
                    flushBuffer(wordIdx);
                    msg.push(
                    <img
                      key={`${line._id}-${idx}-${wordIdx}-${em.id}`}
                      className="emote"
                      src={`/api/vods/emotes/${vodId}/${em.id}`}
                      alt={em.name ?? '-'}
                      title={em.name ?? '-'}
                      width={em.width}
                      height={em.height}
                    />
                    );
                } else if (word.trim().length > 0) {
                    if (urlRegex.test(word)) {
                        flushBuffer(wordIdx);
                        msg.push(
                            <a
                            key={`${line._id}-${idx}-${wordIdx}`}
                            className="word link"
                            href={word}
                            target="_blank"
                            rel="noreferrer"
                            >
                            {word}
                            </a>
                        );
                    } else {
                        buffer.push(word);
                    }
                }
            });

            // flush any trailing buffered words
            flushBuffer(words.length);
        }
    });

    ret.push(
        <div key="timestamp" className="timestamp" onClick={() => onTimestampClicked(line.content_offset_seconds)}>{ts}</div>,
        <div key="badges" className="badges">
            {messageBadges}
        </div>,
        <div key="author" className="author">
            <span style={{ color: line.message.user_color }}>{line.commenter.display_name}</span>:
        </div>,
        ...msg
    );

    return (
        <div key={line._id} className="chat-line">
            {ret}
        </div>
    );
});
